import React, { useEffect, useMemo, useState } from 'react'
import { EyeIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import { Card, CardContent } from '../../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../../config/api'

const SECTION_LABELS = {
  sof: 'Source of funds / wealth',
  activity: 'Activity analysis',
  general: 'General',
}

function docLabel(d) {
  if (typeof d === 'string') return d
  return d?.file_name || d?.name || d?.filename || 'Document'
}

function docId(d) {
  if (d == null || typeof d === 'string') return null
  if (d.id == null || d.id === '') return null
  const n = Number(d.id)
  return Number.isFinite(n) ? String(d.id) : null
}

function normalizeDoc(row, sectionFallback) {
  if (!row) return null
  if (typeof row === 'string') {
    return { file_name: row, section: sectionFallback || 'general', id: null }
  }
  return {
    id: row.id != null ? String(row.id) : null,
    file_name: row.file_name || row.name || row.filename || 'Document',
    mime_type: row.mime_type || null,
    section: row.section || sectionFallback || 'general',
    at: row.uploaded_at || row.at || null,
  }
}

/**
 * Right-rail summary of all ECDD section attachments (SoF, activity, …).
 */
export default function EcddAttachedDocumentsRail({ ecddId, formAnswers, variant = 'default' }) {
  const cardClass =
    variant === 'sidebar'
      ? 'border border-slate-200 shadow-sm overflow-hidden border-t-4 border-t-slate-300 bg-white'
      : 'border border-slate-200 shadow-md overflow-hidden border-t-4 border-t-slate-300 bg-white'
  const [serverDocs, setServerDocs] = useState([])
  const [openingKey, setOpeningKey] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!ecddId) {
        setServerDocs([])
        return
      }
      try {
        const res = await authFetch(
          getLocalApiUrl(`/api/ecdd/${encodeURIComponent(ecddId)}/documents`)
        )
        const j = await res.json().catch(() => ({}))
        if (cancelled || !res.ok || !j.success) return
        setServerDocs(
          (Array.isArray(j.data) ? j.data : [])
            .map((r) => normalizeDoc(r))
            .filter(Boolean)
        )
      } catch {
        /* non-fatal */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [ecddId, formAnswers?.sofDocs, formAnswers?.actDocs])

  const grouped = useMemo(() => {
    const fromForm = [
      ...(Array.isArray(formAnswers?.sofDocs) ? formAnswers.sofDocs.map((d) => normalizeDoc(d, 'sof')) : []),
      ...(Array.isArray(formAnswers?.actDocs) ? formAnswers.actDocs.map((d) => normalizeDoc(d, 'activity')) : []),
    ].filter(Boolean)

    const byKey = new Map()
    const add = (d) => {
      if (!d) return
      const key = d.id
        ? `id:${d.id}`
        : `name:${String(d.section || '')}:${String(d.file_name || '').toLowerCase()}`
      const prev = byKey.get(key)
      byKey.set(key, prev ? { ...prev, ...d, id: d.id || prev.id } : d)
    }
    fromForm.forEach(add)
    serverDocs.forEach(add)

    const all = [...byKey.values()]
    const order = ['sof', 'activity', 'general']
    const map = new Map()
    for (const d of all) {
      const sec = d.section || 'general'
      if (!map.has(sec)) map.set(sec, [])
      map.get(sec).push(d)
    }
    return order
      .filter((s) => map.has(s))
      .concat([...map.keys()].filter((s) => !order.includes(s)))
      .map((section) => ({
        section,
        label: SECTION_LABELS[section] || section,
        docs: map.get(section) || [],
      }))
      .filter((g) => g.docs.length > 0)
  }, [formAnswers?.sofDocs, formAnswers?.actDocs, serverDocs])

  const total = grouped.reduce((n, g) => n + g.docs.length, 0)

  const viewDoc = async (d, index) => {
    const key = `${docId(d) || docLabel(d)}-${index}`
    setOpeningKey(key)
    try {
      let id = docId(d)
      if (!id && ecddId) {
        const match = serverDocs.find(
          (s) =>
            String(s.file_name || '').toLowerCase() === String(docLabel(d)).toLowerCase() &&
            (!d.section || s.section === d.section)
        )
        id = match?.id || null
      }
      if (!ecddId || !id) {
        window.alert('This file cannot be opened yet — open the section and re-upload if needed.')
        return
      }
      const res = await authFetch(
        getLocalApiUrl(
          `/api/ecdd/${encodeURIComponent(ecddId)}/documents/${encodeURIComponent(id)}/download`
        )
      )
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `Open failed (${res.status})`)
      }
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const w = window.open(objectUrl, '_blank', 'noopener,noreferrer')
      if (!w) {
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = docLabel(d)
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (err) {
      window.alert(err.message || 'Could not open document')
    } finally {
      setOpeningKey(null)
    }
  }

  return (
    <Card className={cardClass}>
      <CardContent className={variant === 'sidebar' ? 'p-3.5' : 'p-4'}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="text-sm font-bold text-psg-teal">Attached documents</div>
            <p className="text-xs text-slate-500 m-0 mt-0.5">From all form sections</p>
          </div>
          <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full shrink-0 bg-slate-100 text-slate-600">
            {total}
          </span>
        </div>

        {total === 0 ? (
          <p className="text-xs text-slate-400 m-0">
            No documents attached yet. Upload under Source of funds or Activity analysis.
          </p>
        ) : (
          <div className="space-y-3">
            {grouped.map((g) => (
              <div key={g.section}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                  {g.label}
                </div>
                <ul className="m-0 p-0 list-none space-y-1.5">
                  {g.docs.map((d, i) => {
                    const key = `${docId(d) || docLabel(d)}-${g.section}-${i}`
                    const opening = openingKey === key
                    return (
                      <li
                        key={key}
                        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/80 px-2.5 py-1.5"
                      >
                        <PaperClipIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="flex-1 min-w-0 text-xs text-slate-700 truncate" title={docLabel(d)}>
                          {docLabel(d)}
                        </span>
                        <button
                          type="button"
                          className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-psg-teal hover:underline bg-transparent border-none cursor-pointer shrink-0 disabled:opacity-50"
                          onClick={() => viewDoc(d, i)}
                          disabled={opening || !ecddId}
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                          {opening ? '…' : 'View'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
